import React from 'react';
import MessageCard from '../../../Elements/MessageCard';
import LabeledValueBox from '../../../Elements/LabeledValueBox';
import FigmaButton from '../../../Elements/FigmaButton';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../../../Enums/ButtonTypeEnum';
import IconPositionEnum from '../../../Enums/IconPositionEnum';

function MessageCardDemoComponent() {
  const actions = (
    <>
      {/* FigmaButton always renders a <button>; a primary that is really a link gets wrapped
          in an anchor, the same idiom ButtonRow uses for its own href entries. */}
      <a
        href='https://www.bareinternational.com/'
        target='_blank'
        rel='noopener noreferrer'
      >
        <FigmaButton
          label='Primary action'
          color={ButtonColorsEnum.Primary}
          type={ButtonTypeEnum.NormalIconRight}
          iconName='fa-solid fa-arrow-up-right-from-square'
          iconPosition={IconPositionEnum.Right}
        />
      </a>
      <FigmaButton
        label='Secondary action'
        color={ButtonColorsEnum.Common}
        type={ButtonTypeEnum.NormalIconLeft}
        iconName='fa-solid fa-right-left'
        iconPosition={IconPositionEnum.Left}
        onClick={() => {}}
      />
    </>
  );

  return (
    <div>
      <div className='dsg-row'>
        <div className='dsg-demo-label'>Full card — eyebrow, icon, title, lead + body copy, actions</div>
      </div>
      <div className='dsg-row'>
        <MessageCard
          eyebrow='Eyebrow'
          title='Card title'
          iconName='fa-solid fa-lock'
          actions={actions}
        >
          <p className='mcard-lead'>The lead sentence states what happened, in one line.</p>
          <p className='mcard-text'>
            Supporting copy explains why and what the reader can do about it. Keep it to a short paragraph — a card like
            this usually carries the whole message.
          </p>
        </MessageCard>
      </div>

      <div className='dsg-row'>
        <div className='dsg-demo-label'>With a LabeledValueBox in the body — one fact read back to the user</div>
      </div>
      <div className='dsg-row'>
        <MessageCard
          eyebrow='Bravo access'
          title='Sign-in unavailable'
          iconName='fa-solid fa-lock'
          actions={actions}
        >
          <p className='mcard-lead'>We&rsquo;re unable to sign you in with these credentials.</p>
          <LabeledValueBox
            label='Account in use'
            value='firstname.lastname@bareinternational.com'
            iconName='fa-solid fa-envelope'
          />
        </MessageCard>
      </div>

      <div className='dsg-row'>
        <div className='dsg-demo-label'>Minimal — title and body only (every part except the body is optional)</div>
      </div>
      <div className='dsg-row'>
        <MessageCard title='Nothing to do here'>
          <p className='mcard-text'>
            Without an icon the title sits flush left; without actions the footer is not rendered at all.
          </p>
        </MessageCard>
      </div>
    </div>
  );
}

export const messageCardDemo = {
  id: 'message-card',
  title: 'Message card',
  category: 'Cards',
  importPath: 'Elements/MessageCard (+ Elements/LabeledValueBox)',
  notes: [
    'A card that carries one message: a banner, an icon tile, an eyebrow, an Oswald title, body copy and a row of actions. Props: eyebrow, title, iconName, children, actions, className — all optional except children.',
    'It is the DS .confirm-* family as a component (.confirm-card / .confirm-banner / .confirm-head / .confirm-icon.tone-navy / .confirm-body / .confirm-foot), so the banner, the icon tint and the dark-mode overrides come from bravo-ds.css rather than being restated. FigmaPopup renders the same anatomy inline for modals — use this one on a page.',
    'Body copy: .mcard-lead for the opening sentence (bold, --ink), .mcard-text for the rest (--ink-2). Anything else can go in the body too — LabeledValueBox is the usual companion.',
    'Actions are laid edge to edge and weighted 1.6:1 toward the FIRST one, which is treated as the primary: a card standing on its own has no toolbar to sit in, so a left-packed row leaves dead space at its right edge. On a phone they stack full-width at a 46px hit target.',
    'FigmaButton always renders a <button>, so a primary that is really a link must be wrapped in an anchor — the same idiom ButtonRow uses for its own href entries.',
    'It caps at 560px (the DS .confirm-card is a modal and caps at 640px). To put one on a page of its own, see the Standalone page entry under Navigation & Layout.',
  ],
  snippet:
    "<MessageCard eyebrow='Bravo access' title='Sign-in unavailable' iconName='fa-solid fa-lock' actions={actions}>\n  <p className='mcard-lead'>We're unable to sign you in with these credentials.</p>\n  <p className='mcard-text'>...</p>\n  <LabeledValueBox label='Account in use' value={accountName} iconName='fa-solid fa-envelope' />\n</MessageCard>",
  render: MessageCardDemoComponent,
};
